/**
 * Acknowledge and Verified Model - Encapsulates verification API response data
 */

interface AcknowledgeAndVerifiedServerData {
    reconciliation_date: string;
    amount: string;
    reason: string;
    is_verified: boolean;
    is_ack: boolean;
    notes: string | null;
    audit_status?: string | null;
    manager_note?: Array<Record<string, any>> | null;
    manager_note_user_name?: string | null;
    manager_note_at?: string | null;
    verified_by_user_name?: string | null;
    verified_at?: string | null;
    verification_note?: Array<Record<string, any>> | null;
    can_add_manager_note?: boolean | null;
    can_verify?: boolean | null;
    cashier_shift_notes?: Array<Record<string, any>> | null;
    id: string;
    store_id: string;
    user_id: string;
    audit_data_snapshot: any;
    created_at: string;
    updated_at: string;
}

class AcknowledgeAndVerifiedModel {
    private _serverData: AcknowledgeAndVerifiedServerData | null;

    constructor(serverData: AcknowledgeAndVerifiedServerData | null = null) {
        this._serverData = serverData;
    }

    get hasServerData(): boolean {
        return this._serverData !== null;
    }

    get isVerified(): boolean {
        return this._serverData?.is_verified === true || this._serverData?.audit_status === 'approved';
    }

    get auditStatus(): string {
        if (this._serverData?.audit_status) {
            return this._serverData.audit_status;
        }
        return this.isVerified ? 'approved' : 'matched';
    }

    get isAck(): boolean {
        return this._serverData?.is_ack || false;
    }

    get notes(): string {
        return this._serverData?.notes || '';
    }

    private _latestNoteText(notes?: Array<Record<string, any>> | null): string {
        if (!Array.isArray(notes) || notes.length === 0) {
            return '';
        }

        const latest = notes[notes.length - 1];
        return String(latest?.note || '').trim();
    }

    get managerNotes(): Array<Record<string, any>> {
        return Array.isArray(this._serverData?.manager_note) ? this._serverData!.manager_note! : [];
    }

    get managerNote(): string {
        return this._latestNoteText(this._serverData?.manager_note);
    }

    get managerNoteByUserName(): string {
        return this._serverData?.manager_note_user_name || '';
    }

    get managerNoteAt(): string {
        return this._serverData?.manager_note_at || '';
    }

    get verificationNotes(): Array<Record<string, any>> {
        return Array.isArray(this._serverData?.verification_note) ? this._serverData!.verification_note! : [];
    }

    get verificationNote(): string {
        return this._latestNoteText(this._serverData?.verification_note) || this.notes;
    }

    get verifiedByUserName(): string {
        return this._serverData?.verified_by_user_name || '';
    }

    get verifiedAt(): string {
        return this._serverData?.verified_at || '';
    }

    get canAddManagerNote(): boolean {
        return this._serverData?.can_add_manager_note === true;
    }

    get canVerify(): boolean {
        return this._serverData?.can_verify === true;
    }

    get cashierShiftNotes(): Array<Record<string, any>> {
        return Array.isArray(this._serverData?.cashier_shift_notes) ? this._serverData!.cashier_shift_notes! : [];
    }

    get hasNotes(): boolean {
        return this.verificationNote.length > 0 || this.managerNote.length > 0;
    }

    get amount(): string {
        return this._serverData?.amount || '0.00';
    }

    get reason(): string {
        return this._serverData?.reason || '';
    }

    get date(): string {
        return this._serverData?.reconciliation_date || '';
    }

    static fromServerData(serverData: AcknowledgeAndVerifiedServerData | null): AcknowledgeAndVerifiedModel {
        return new AcknowledgeAndVerifiedModel(serverData);
    }

    static createEmpty(): AcknowledgeAndVerifiedModel {
        return new AcknowledgeAndVerifiedModel();
    }
}

export default AcknowledgeAndVerifiedModel;
export type { AcknowledgeAndVerifiedServerData };
